import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Scene, SceneViewLabel } from "./components/object-draw/scene";
import { Button } from "@mui/material";
import { MetricsSummary } from "./components/panels/pnl_metrics_summary";
import { FormModelloFinitura } from "./components/forms/frm_modello_finitura";
import { useStoreDispatch, useStoreSelector } from "../../../../../hooks/StoreHooks";
import { getCurrentUser, isGlasspanelSelection, toDrawUnit } from "../../../../core/common_functions";
import { useDoorConfigurationBuilderActions } from "../../../../../store/reducers/door_configuration_builder";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useDialogs } from "../../../../providers/dialogs_provider";
import { DoorType, FinishType } from "../../../../core/common_types";
import { useOverlapConfigurationsService } from "../../../../services/overlap_configurations_service";
import { FormAspettoModel, Option } from "../../../../models/door_configuration";
import { PROFONDITA_INVOLUCRO } from "../../../../core/common_constants";
import { GarageExternal } from "./components/object-draw/environment/garage_external";
import PortaOverlap from "../component/door-compont-3d/PortaOverlap";

export const AspectPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { base, doorConfigurationBuilder } = useStoreSelector(store => store);
    const dispatcher = useStoreDispatch();
    const currentUser = getCurrentUser(base);
    const { setStepActive, setStepComplete, setStepVisible } = useDoorConfigurationBuilderActions();
    const dialogs = useDialogs();
    const [isSubmit, setSubmit] = useState<boolean>(false);
    const configurationService = useOverlapConfigurationsService();
    const sceneContainerRef = useRef<HTMLDivElement>(null);

    const findFinitura = (): Option|null|undefined => {
      if (!doorConfigurationBuilder.configuration) return null;

      if (!doorConfigurationBuilder.configuration.finitura) return null;
      return doorConfigurationBuilder.options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.finitura);
    }

    const findModello = (): Option|null|undefined => {
      if (!doorConfigurationBuilder.configuration?.modello) return null;
      return doorConfigurationBuilder.options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.modello);
    }

    const hasVetratura = (finitura: Option|null|undefined): boolean => {
      if (!finitura) return false;
      return doorConfigurationBuilder.options.some(o => o?.type == "VETRATURA" && o?.parentId == finitura.id);
    }

    const isFullglass = (finitura: Option|null|undefined): boolean => {
      if (!finitura) return false;
      return isGlasspanelSelection(findModello()?.name, finitura.name);
    }

    const isFullVision = (finitura: Option|null|undefined): boolean => {
      if (!finitura) return false;
      return finitura.name == FinishType.full_vision;
    }

    const isSpecial = (finitura: Option|null|undefined): boolean => {
      if (!finitura) return false;
      return finitura.name == FinishType.special;
    }

    const submit = async () => {  
      setSubmit(true);
      try {
        let finitura = findFinitura();

        if (!doorConfigurationBuilder.configuration) throw "invalid configuration";
        if (!doorConfigurationBuilder.configuration.porta) throw {severity: "warning", message: "Scegli il tipo di porta"};
        if (!doorConfigurationBuilder.configuration.modello) throw {severity: "warning", message: "Scegli il modello"};
        if (!doorConfigurationBuilder.configuration.finitura) throw {severity: "warning", message: "Scegli la finitura"};
        if (doorConfigurationBuilder.configuration.tipoColoreEsterno == "ral_standard" && !doorConfigurationBuilder.configuration.coloreEsterno) throw {severity: "warning", message: "Seleziona il colore esterno"};
        if (doorConfigurationBuilder.configuration.tipoColoreEsterno == "ral_non_standard" && !doorConfigurationBuilder.configuration.coloreEsternoNsRal) throw {severity: "warning", message: "Inserisci il RAL da usare come colore esterno"};
        
        if (doorConfigurationBuilder.configuration.tipoColoreInterno == "ral_standard" && !doorConfigurationBuilder.configuration.coloreInterno && (doorConfigurationBuilder.isGlasspanel() && doorConfigurationBuilder.configuration.coloreTelaioInternoDiverso)) throw {severity: "warning", message: "Seleziona il colore interno"};
        if (doorConfigurationBuilder.configuration.tipoColoreInterno == "ral_non_standard" && !doorConfigurationBuilder.configuration.coloreInternoNsRal && (doorConfigurationBuilder.isGlasspanel() && doorConfigurationBuilder.configuration.coloreTelaioInternoDiverso)) throw {severity: "warning", message: "Inserisci il RAL da usare come colore interno"};
        
        if (isFullglass(finitura) || isFullVision(finitura)) {
          if (doorConfigurationBuilder.configuration.coloreTelaioInternoDiverso) {
            if (doorConfigurationBuilder.configuration.tipoColoreInterno == "ral_standard" && !doorConfigurationBuilder.configuration.coloreInterno) throw {severity: "warning", message: "Seleziona il colore del telaio interno"};
            if (doorConfigurationBuilder.configuration.tipoColoreInterno == "ral_non_standard" && !doorConfigurationBuilder.configuration.coloreInternoNsRal) throw {severity: "warning", message: "Inserisci il RAL da usare per il colore del telaio interno"};
          }

          if (hasVetratura(finitura) && !doorConfigurationBuilder.configuration.finituraVetro) throw {severity: "warning", message: "Scegli la finitura del vetro"};
        }
        
        let data: FormAspettoModel = {
          porta: doorConfigurationBuilder.configuration.porta,
          modello: doorConfigurationBuilder.configuration.modello,
          finitura: doorConfigurationBuilder.configuration.finitura,
          tipoColoreEsterno: doorConfigurationBuilder.configuration.tipoColoreEsterno,
          coloreEsterno: doorConfigurationBuilder.configuration.coloreEsterno,
          coloreEsternoNsRal: doorConfigurationBuilder.configuration.coloreEsternoNsRal,
          coloreEsternoACampione: doorConfigurationBuilder.configuration.coloreEsternoACampione,
          tipoColoreInterno: doorConfigurationBuilder.configuration.tipoColoreInterno,
          coloreInterno: doorConfigurationBuilder.configuration.coloreInterno,
          coloreInternoNsRal: doorConfigurationBuilder.configuration.coloreInternoNsRal,
          coloreInternoACampione: doorConfigurationBuilder.configuration.coloreInternoACampione,
          finituraVetro: doorConfigurationBuilder.configuration.finituraVetro,
          coloreTelaioInternoDiverso: doorConfigurationBuilder.configuration.coloreTelaioInternoDiverso,
          finestratura: doorConfigurationBuilder.configuration.finestratura,
          ventilazione: doorConfigurationBuilder.configuration.ventilazione,
          portinaIntegrata: doorConfigurationBuilder.configuration.portinaIntegrata,
          portinaAffiancata: doorConfigurationBuilder.configuration.portinaAffiancata,
          artCoatPreviewImage: doorConfigurationBuilder.configuration.artCoatPreviewImage,
        };

        if ((isFullglass(finitura) || isFullVision(finitura)) && !data.coloreTelaioInternoDiverso) {
          data.coloreInterno = data.coloreEsterno;
          data.coloreInternoNsRal = data.coloreEsternoNsRal;
          data.coloreInternoACampione = data.coloreEsternoACampione;
        }
  
        let res = await configurationService.updateAspectOver(currentUser, doorConfigurationBuilder.configuration.configurationId, data);

        setSubmit(false);
          
        let porta = doorConfigurationBuilder.options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.porta);
        let activateFinestratura = (porta && finitura && porta.name != DoorType.full_vision && !isFullglass(finitura)) as boolean;
        dispatcher(setStepComplete({ stepSlug: "aspetto", isComplete: true }));
        dispatcher(setStepVisible({ stepSlug: "finestratura", isVisible: activateFinestratura }));
        dispatcher(setStepVisible({ stepSlug: "ventilazione", isVisible: activateFinestratura }));
        dispatcher(setStepVisible({ stepSlug: "portina-pedonale", isVisible: res?.integratedDoorSupport ?? false }));
        
        if (res?.integratedDoorSupport) {
          navigate(`/overlap-configurations/${id}/portina-pedonale`);
        } else {
          navigate(`/overlap-configurations/${id}/maniglieria`);
        }
      } catch (e: any) {
        setSubmit(false);

        if (typeof e == "string") {
          dialogs?.showError("Attenzione...", e);
        } else {
          if (e.severity == "error") {
            if (e.message == "UNAVAILABLE") {
              let msg = "Siamo spiacenti, le dimensioni inserite non rientrano nei limiti di questa gamma/finitura o tipolgia";
              dialogs?.showError("Attenzione...", msg);
            } else {
              dialogs?.showError("Attenzione...", e.message);
            }
          } else if (e.severity == "warning") {
            dialogs?.showWarning("Attenzione...", e.message);
          }
        }
      }
    }

    useEffect(() => {
        if (!id || isNaN(Number(id)) || !doorConfigurationBuilder.configuration) {
            navigate("/configurations");
        } else {
            dispatcher(setStepActive({ stepSlug: "aspetto", isActive: true }));
        }
    }, []);

    return (
        <div className="row h-full m-0">
            <div ref={sceneContainerRef} className="col-xl-7 p-0 disegn-render" style={{ position: "relative", height: "100%" }}>
                {doorConfigurationBuilder.configuration && (
                  <PortaOverlap />
                )}
            </div>
            <div className="col-xl-5 p-0" style={{ overflowY: "auto", height: "100%", borderLeft: "solid 1px #ccc" }}>                
                <div className="p-4">                        
                    <FormModelloFinitura options={doorConfigurationBuilder.options} colors={doorConfigurationBuilder.colors} />

                    <div className="d-flex flex-row-reverse justify-content-between mt-3">
                        <Button disabled={isSubmit} variant="contained" className="ps-3 pe-1" onClick={() => submit()}>
                            <span>Avanti</span>
                            <NavigateNextIcon />
                        </Button>

                        <Button variant="contained" className="ps-1 pe-3" color="secondary" onClick={() => {
                            navigate(`/overlap-configurations/${doorConfigurationBuilder.configuration?.configurationId}/dimensioni`);
                        }}>
                            <NavigateBeforeIcon />
                            <span>Indietro</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}